// View.jsx
import React, { useEffect, useMemo, useState, useCallback } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { searchContent } from '@plone/volto/actions';
import useEmblaCarousel from 'embla-carousel-react';
import Autoplay from 'embla-carousel-autoplay';
import './events.css';

/* ---------- helpers ---------- */
const ensureLeadingSlash = (p) => (p ? (p.startsWith('/') ? p : `/${p}`) : '');

const rawToDate = (v) => {
  if (!v) return null;
  const d = v instanceof Date ? v : new Date(v);
  return isNaN(d.getTime()) ? null : d;
};

export const startDateOf = (item) =>
  rawToDate(item.start || item.start_date || item.effective || item.Date) ||
  rawToDate(item.end || item.end_date || item.expires || item.ExpirationDate);

export const endDateOf = (item) =>
  rawToDate(item.end || item.end_date || item.expires || item.ExpirationDate);

export const isUpcomingOrOngoing = (item, now = new Date()) => {
  const s = startDateOf(item);
  const e = endDateOf(item);
  if (e) return e >= now;
  if (s) return s >= now;
  return false;
};

export const toEvent = (item, data = {}) => {
  const image =
    item.image ||
    (item.image_field && item.image_scales
      ? `${item['@id']}/@@images/${item.image_field}`
      : undefined) ||
    (item.preview_image
      ? `${item.preview_image['@id']}/@@images/image`
      : undefined) ||
    (item.lead_image ? `${item.lead_image['@id']}/@@images/image` : undefined);
  return {
    id: item['@id'] || item.id,
    title: item.title,
    description: item.description || item.Description,
    image,
    start: item.start || item.start_date || item.effective || item.Date,
    end: item.end || item.end_date || item.expires || item.ExpirationDate,
    location: item.location || item.place || item.city || '',
    url: item.url || item['@id'] || '#',
    buttonText: data.buttonText || 'View event',
  };
};

export const byStartAsc = (a, b) => {
  const sa = startDateOf(a);
  const sb = startDateOf(b);
  if (sa && sb) return sa - sb;
  if (sa) return -1;
  if (sb) return 1;
  return 0;
};
export const byStartDesc = (a, b) => -byStartAsc(a, b);

export const formatDay = (d) =>
  d.toLocaleDateString('en-GB', {
    weekday: 'long',
    year: 'numeric',
    month: 'short',
    day: '2-digit',
  });

const isMidnight = (d) =>
  d && d.getHours() === 0 && d.getMinutes() === 0 && d.getSeconds() === 0;
const isEndOfDay = (d) => d && d.getHours() === 23 && d.getMinutes() === 59;
const sameDay = (a, b) => !!a && !!b && a.toDateString() === b.toDateString();

export const formatListWhen = (start, end) => {
  const s = rawToDate(start);
  const e = rawToDate(end);
  if (!s && !e) return '';

  const dateFmt = (d) =>
    d.toLocaleDateString('en-GB', {
      day: '2-digit',
      month: 'short',
      year: 'numeric',
    });
  const timeFmt = (d) =>
    d.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });

  if (s && e) {
    const same = sameDay(s, e);
    const startHasTime = !isMidnight(s);
    const endHasTime = !(isMidnight(e) || isEndOfDay(e));

    if (!startHasTime && !endHasTime) return `${dateFmt(s)} - ${dateFmt(e)}`;
    if (same) {
      const st = startHasTime ? timeFmt(s) : '00:00';
      const et = endHasTime ? timeFmt(e) : '';
      return et ? `${dateFmt(s)}, ${st} - ${et}` : `${dateFmt(s)}, ${st}`;
    }
    if (startHasTime && endHasTime) {
      return `${dateFmt(s)}, ${timeFmt(s)} - ${dateFmt(e)}, ${timeFmt(e)}`;
    }
    return `${dateFmt(s)} - ${dateFmt(e)}`;
  }

  if (s && !e) return `${dateFmt(s)}${!isMidnight(s) ? ', ' + timeFmt(s) : ''}`;
  if (!s && e)
    return `${dateFmt(e)}${!(isMidnight(e) || isEndOfDay(e)) ? ', ' + timeFmt(e) : ''}`;
  return '';
};

export const formatAgendaWhen = (start, end) => {
  const s = rawToDate(start);
  const e = rawToDate(end);
  if (!s && !e) return '';

  const startHasTime = !!(s && !isMidnight(s));
  const endHasTime = !!(e && !(isMidnight(e) || isEndOfDay(e)));
  const allDay = !!(
    s &&
    !startHasTime &&
    (!e || !endHasTime || isEndOfDay(e) || isMidnight(e))
  );
  const oneDay = !!(s && e && sameDay(s, e));

  if (allDay && oneDay) return '';
  if (allDay && e && s && !oneDay) {
    let endAdj = e;
    if (isMidnight(e) && e > s)
      endAdj = new Date(e.getTime() - 24 * 60 * 60 * 1000);
    const ed = endAdj.toLocaleDateString('en-GB', {
      day: '2-digit',
      month: 'short',
      year: 'numeric',
    });
    return `Ends on ${ed}`;
  }
  if (s && e && oneDay && (startHasTime || endHasTime)) {
    const st = s.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
    const et = endHasTime
      ? e.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })
      : null;
    return et ? `${st} – ${et}` : `Starts at ${st}`;
  }
  if (s && e && !oneDay) {
    const st = startHasTime
      ? s.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })
      : null;
    const ed = e.toLocaleDateString('en-GB', {
      day: '2-digit',
      month: 'short',
      year: 'numeric',
    });
    const et = endHasTime
      ? e.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })
      : null;
    if (st && et) return `Starts at ${st} | Ends on ${ed} ${et}`;
    if (st && !et) return `Starts at ${st} | Ends on ${ed}`;
    if (!st && et) return `Ends on ${ed} ${et}`;
    return `Ends on ${ed}`;
  }
  if (s && !e) {
    if (!isMidnight(s))
      return `Starts at ${s.toLocaleTimeString([], {
        hour: '2-digit',
        minute: '2-digit',
      })}`;
    return '';
  }
  if (!s && e) {
    const ed = e.toLocaleDateString('en-GB', {
      day: '2-digit',
      month: 'short',
      year: 'numeric',
    });
    const et = endHasTime
      ? e.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })
      : null;
    return et ? `Ends on ${ed} ${et}` : `Ends on ${ed}`;
  }
  return '';
};

/* ---------- Presentational subcomponents ---------- */
const List = ({ events, data }) => (
  <ul className={`events-list ${data.showImage ? 'with-image' : 'no-image'}`}>
    {events.map((ev) => (
      <li key={ev.id} className="events-list-item">
        {data.showImage && ev.image ? (
          <a href={ev.url} className="events-thumb">
            <img src={ev.image} alt="" />
          </a>
        ) : null}
        <div className="events-body">
          <h3 className="events-title">
            <a href={ev.url}>{ev.title}</a>
          </h3>
          {data.showDate && (ev.start || ev.end) && (
            <div className="events-when">
              {formatListWhen(ev.start, ev.end)}
            </div>
          )}
          {data.showLocation && ev.location && (
            <div className="events-location">{ev.location}</div>
          )}
          {data.showDescription && ev.description && (
            <p className="events-desc">{ev.description}</p>
          )}
          <p className="events-cta">
            <a className="events-button" href={ev.url}>
              {ev.buttonText}
            </a>
          </p>
        </div>
      </li>
    ))}
  </ul>
);

const Carousel = ({ events, data }) => {
  const slidesToShow =
    typeof window !== 'undefined' &&
    window.matchMedia('(max-width: 768px)').matches
      ? 1
      : parseInt(data.slidesToShow, 10) || 1;

  const plugins = data.autoplay
    ? [Autoplay({ delay: parseInt(data.autoplayDelay, 10) || 6000 })]
    : [];
  const [viewportRef, embla] = useEmblaCarousel(
    { loop: data.loop ?? true, align: 'start', slidesToScroll: slidesToShow },
    plugins,
  );

  const [selectedIndex, setSelectedIndex] = useState(0);
  const [scrollSnaps, setScrollSnaps] = useState([]);

  const onSelect = useCallback(() => {
    if (!embla) return;
    setSelectedIndex(embla.selectedScrollSnap());
  }, [embla]);

  useEffect(() => {
    if (!embla) return;
    setScrollSnaps(embla.scrollSnapList());
    onSelect();
    embla.on('select', onSelect);
    embla.on('reInit', () => {
      setScrollSnaps(embla.scrollSnapList());
      onSelect();
    });
  }, [embla, onSelect]);

  const scrollTo = (idx) => embla && embla.scrollTo(idx);

  return (
    <div className={`embla ${data.equalHeight ? 'equal-height' : ''}`}>
      <div className="embla__viewport" ref={viewportRef}>
        <div className="embla__container">
          {events.map((ev) => (
            <div
              key={ev.id}
              className="embla__slide"
              style={{ flex: `0 0 ${100 / slidesToShow}%` }}
            >
              <a className="carousel-slide-inner clickable" href={ev.url}>
                <h3>{ev.title}</h3>
                {data.showDate && (ev.start || ev.end) && (
                  <p className="events-when">
                    {formatListWhen(ev.start, ev.end)}
                  </p>
                )}
                {data.showDescription && ev.description && (
                  <p>{ev.description}</p>
                )}
              </a>
            </div>
          ))}
        </div>
        {data.blockTitle ? (
          <h2 className="embla__title events-block-title">{data.blockTitle}</h2>
        ) : null}
      </div>

      {!data.hideArrows && events.length > slidesToShow && (
        <div className="embla__controls" aria-hidden="false">
          <p>
            Don’t miss what’s making waves next. Your calendar of can’t miss
            meets.
          </p>
          <div className="embla__controls__buttons" aria-hidden="false">
            <button
              type="button"
              className="embla__prev"
              onClick={() => embla && embla.scrollPrev()}
              aria-label="Previous slide"
            >
              ‹
            </button>
            <button
              type="button"
              className="embla__next"
              onClick={() => embla && embla.scrollNext()}
              aria-label="Next slide"
            >
              ›
            </button>
          </div>
          <p>
            <a href="/events">View all our upcoming events &gt;</a>
          </p>
        </div>
      )}

      {!data.hideDots && scrollSnaps.length > 1 && (
        <div className="embla__dots">
          {scrollSnaps.map((_, i) => (
            <button
              type="button"
              key={i}
              className={`embla__dot ${i === selectedIndex ? 'is-selected' : ''}`}
              onClick={() => scrollTo(i)}
              aria-label={`Go to slide ${i + 1}`}
            />
          ))}
        </div>
      )}
    </div>
  );
};

const Agenda = ({ events }) => {
  const groups = events.reduce((acc, ev) => {
    const s = startDateOf(ev);
    if (!s) return acc;
    const key = `${s.getFullYear()}-${String(s.getMonth() + 1).padStart(
      2,
      '0',
    )}-${String(s.getDate()).padStart(2, '0')}`;
    acc[key] = acc[key] || { date: s, items: [] };
    acc[key].items.push(ev);
    return acc;
  }, {});
  const dayKeys = Object.keys(groups).sort((a, b) => (a < b ? -1 : 1));
  return (
    <div className="events-agenda">
      {dayKeys.map((k) => {
        const { date, items } = groups[k];
        const sorted = items.sort(byStartAsc);
        return (
          <section key={k} className="agenda-day">
            <h4 className="agenda-heading">{formatDay(date)}</h4>
            <ul className="agenda-list">
              {sorted.map((ev, idx) => {
                const parity = idx % 2 === 0 ? 'even' : 'odd';
                const line = formatAgendaWhen(ev.start, ev.end);
                return (
                  <li key={ev.id} className={`agenda-item ${parity}`}>
                    <div className="agenda-body">
                      <h5 className="agenda-title">
                        <a href={ev.url}>{ev.title}</a>
                      </h5>
                      {line ? <div className="agenda-when">{line}</div> : null}
                      {ev.location ? (
                        <div className="agenda-location">{ev.location}</div>
                      ) : null}
                      {ev.description ? (
                        <p className="agenda-desc">{ev.description}</p>
                      ) : null}
                    </div>
                  </li>
                );
              })}
            </ul>
          </section>
        );
      })}
    </div>
  );
};

/* ---- All events with pagination and (optional) year accordion ---- */
const AllEvents = ({ events, accordion, pageSize = 0 }) => {
  const [page, setPage] = useState(0);
  const eventsDesc = useMemo(() => [...events].sort(byStartDesc), [events]);
  const total = eventsDesc.length;
  const size = parseInt(pageSize, 10) || 0;
  const totalPages = size > 0 ? Math.max(1, Math.ceil(total / size)) : 1;

  useEffect(() => {
    setPage(0);
  }, [size, total]);

  const slice = useMemo(() => {
    if (size <= 0) return eventsDesc;
    const start = page * size;
    return eventsDesc.slice(start, start + size);
  }, [eventsDesc, page, size]);

  const Pager = () => {
    if (size <= 0 || totalPages <= 1) return null;
    return (
      <nav className="events-pager" aria-label="Events pagination">
        <button
          type="button"
          onClick={() => setPage((p) => Math.max(0, p - 1))}
          disabled={page === 0}
        >
          Previous
        </button>
        <span className="events-pager-status">
          Page {page + 1} of {totalPages}
        </span>
        <button
          type="button"
          onClick={() => setPage((p) => Math.min(totalPages - 1, p + 1))}
          disabled={page >= totalPages - 1}
        >
          Next
        </button>
      </nav>
    );
  };

  if (!accordion) {
    const groups = slice.reduce((acc, ev) => {
      const s = startDateOf(ev);
      if (!s) return acc;
      const key = `${s.getFullYear()}-${String(s.getMonth() + 1).padStart(
        2,
        '0',
      )}-${String(s.getDate()).padStart(2, '0')}`;
      acc[key] = acc[key] || { date: s, items: [] };
      acc[key].items.push(ev);
      return acc;
    }, {});
    const dayKeys = Object.keys(groups).sort((a, b) => (a > b ? -1 : 1));
    return (
      <>
        <div className="events-agenda">
          {dayKeys.map((k) => {
            const { date, items } = groups[k];
            const sorted = items.sort(byStartDesc);
            return (
              <section key={k} className="agenda-day">
                <h4 className="agenda-heading">{formatDay(date)}</h4>
                <ul className="agenda-list">
                  {sorted.map((ev, idx) => {
                    const parity = idx % 2 === 0 ? 'even' : 'odd';
                    const line = formatAgendaWhen(ev.start, ev.end);
                    return (
                      <li key={ev.id} className={`agenda-item ${parity}`}>
                        <div className="agenda-body">
                          <h5 className="agenda-title">
                            <a href={ev.url}>{ev.title}</a>
                          </h5>
                          {line ? (
                            <div className="agenda-when">{line}</div>
                          ) : null}
                          {ev.location ? (
                            <div className="agenda-location">{ev.location}</div>
                          ) : null}
                          {ev.description ? (
                            <p className="agenda-desc">{ev.description}</p>
                          ) : null}
                        </div>
                      </li>
                    );
                  })}
                </ul>
              </section>
            );
          })}
        </div>
        <Pager />
      </>
    );
  }

  // Accordion by year
  const byYear = slice.reduce((acc, ev) => {
    const s = startDateOf(ev);
    const y = s ? s.getFullYear() : 'Unknown';
    acc[y] = acc[y] || [];
    acc[y].push(ev);
    return acc;
  }, {});
  const years = Object.keys(byYear).sort((a, b) =>
    parseInt(a) > parseInt(b) ? -1 : 1,
  );

  return (
    <>
      <div className="eea-accordion" data-accordion-group="events-by-year">
        {years.map((year, idx) => {
          const perYear = byYear[year];
          const groups = perYear.reduce((acc, ev) => {
            const s = startDateOf(ev);
            if (!s) return acc;
            const key = `${s.getFullYear()}-${String(s.getMonth() + 1).padStart(
              2,
              '0',
            )}-${String(s.getDate()).padStart(2, '0')}`;
            acc[key] = acc[key] || { date: s, items: [] };
            acc[key].items.push(ev);
            return acc;
          }, {});
          const dayKeys = Object.keys(groups).sort((a, b) => (a > b ? -1 : 1));
          const isOpen = idx === 0;

          return (
            <section
              key={year}
              className={`eea-accordion__item ${isOpen ? 'is-open' : ''}`}
            >
              <h3 className="eea-accordion__header">
                <button
                  id={`acc-trigger-${year}`}
                  className="eea-accordion__trigger"
                  aria-expanded={isOpen}
                  aria-controls={`acc-panel-${year}`}
                  onClick={(e) => {
                    const panel = document.getElementById(`acc-panel-${year}`);
                    const expanded =
                      e.currentTarget.getAttribute('aria-expanded') === 'true';
                    e.currentTarget.setAttribute(
                      'aria-expanded',
                      String(!expanded),
                    );
                    if (panel) panel.hidden = expanded;
                    e.currentTarget
                      .closest('.eea-accordion__item')
                      ?.classList.toggle('is-open');
                  }}
                  type="button"
                >
                  <span className="eea-accordion__title">
                    {year}{' '}
                    <span className="eea-accordion__count" aria-hidden="true">
                      ({perYear.length})
                    </span>
                  </span>
                  <span className="eea-accordion__chevron" aria-hidden="true" />
                </button>
              </h3>

              <div
                id={`acc-panel-${year}`}
                className="eea-accordion__panel"
                role="region"
                aria-labelledby={`acc-trigger-${year}`}
                hidden={!isOpen}
              >
                {dayKeys.map((k) => {
                  const { date, items } = groups[k];
                  const sorted = items.sort(byStartDesc);
                  return (
                    <section key={k} className="agenda-day">
                      <h4 className="agenda-heading">{formatDay(date)}</h4>
                      <ul className="agenda-list">
                        {sorted.map((ev, idx2) => {
                          const parity = idx2 % 2 === 0 ? 'even' : 'odd';
                          const line = formatAgendaWhen(ev.start, ev.end);
                          return (
                            <li key={ev.id} className={`agenda-item ${parity}`}>
                              <div className="agenda-body">
                                <h5 className="agenda-title">
                                  <a href={ev.url}>{ev.title}</a>
                                </h5>
                                {line ? (
                                  <div className="agenda-when">{line}</div>
                                ) : null}
                                {ev.location ? (
                                  <div className="agenda-location">
                                    {ev.location}
                                  </div>
                                ) : null}
                                {ev.description ? (
                                  <p className="agenda-desc">
                                    {ev.description}
                                  </p>
                                ) : null}
                              </div>
                            </li>
                          );
                        })}
                      </ul>
                    </section>
                  );
                })}
              </div>
            </section>
          );
        })}
      </div>
      <Pager />
    </>
  );
};

/* ---------- Main View ---------- */
const View = ({ data = {}, id }) => {
  const dispatch = useDispatch();

  const mode = data.mode || 'list';
  const limit = parseInt(data.limit, 10) || 0;

  // Use EXACT path typed (no prefix, no fallback)
  const pathStr = useMemo(() => {
    const raw = typeof data.navPath === 'string' ? data.navPath.trim() : '';
    return raw ? ensureLeadingSlash(raw) : '';
  }, [data.navPath]);

  // Tags (Plone's Subject). Leave empty for no restriction.
  const tagsFilter = useMemo(() => {
    const raw = typeof data.tagFilter === 'string' ? data.tagFilter.trim() : '';
    if (!raw) return null;
    const arr = raw
      .split(/[,;]+/)
      .map((s) => s.trim())
      .filter(Boolean);
    return arr.length ? arr : null;
  }, [data.tagFilter]);

  const portalTypes = useMemo(() => ['Event'], []);
  const overfetch = useMemo(
    () =>
      mode === 'all' ? 2000 : limit > 0 ? Math.max(limit * 12, 120) : 2000,
    [mode, limit],
  );

  const baseQuery = useMemo(
    () => ({
      portal_type: portalTypes,
      b_size: data.b_size || overfetch,
      sort_on: 'start',
      sort_order: 'ascending',
      metadata_fields: [
        'title',
        'description',
        'image_field',
        'image_scales',
        'effective',
        'start',
        'end',
        'location',
        'Subject',
      ],
      ...(pathStr ? { path: pathStr } : {}),
      ...(tagsFilter ? { Subject: tagsFilter } : {}),
      ...(data.review_state?.length ? { review_state: data.review_state } : {}),
    }),
    [
      portalTypes,
      data.b_size,
      overfetch,
      pathStr,
      tagsFilter,
      data.review_state,
    ],
  );

  const queryKey = useMemo(() => JSON.stringify(baseQuery), [baseQuery]);
  const subkey = useMemo(() => `events-${id || 'block'}`, [id]);

  const slice = useSelector((s) => s.search?.subrequests?.[subkey] || {});
  const raw = useMemo(() => slice.items || [], [slice.items]);
  const loading = !!slice.loading;
  const error = slice.error;

  useEffect(() => {
    const q = JSON.parse(queryKey);
    dispatch(searchContent('', q, subkey));
  }, [dispatch, queryKey, subkey]);

  const mapped = useMemo(() => raw.map((it) => toEvent(it, data)), [raw, data]);

  const baseSorted = useMemo(() => {
    if (mode === 'all') return [...mapped].sort(byStartDesc);
    const now = new Date();
    return mapped.filter((ev) => isUpcomingOrOngoing(ev, now)).sort(byStartAsc);
  }, [mapped, mode]);

  const limited =
    mode === 'all'
      ? baseSorted
      : limit > 0
        ? baseSorted.slice(0, limit)
        : baseSorted;

  if (loading && !raw.length)
    return (
      <div className={`events-block events-block--${mode}`}>
        <div className="events-empty">Loading…</div>
      </div>
    );
  if (error)
    return (
      <div className={`events-block events-block--${mode}`}>
        <div className="events-empty">Error: {String(error)}</div>
      </div>
    );
  if (!baseSorted.length)
    return (
      <div className={`events-block events-block--${mode}`}>
        <div className="events-empty">
          No events. Check dates, path or tags.
        </div>
      </div>
    );

  return (
    <div className={`events-block events-block--${mode}`}>
      {mode === 'carousel' && <Carousel events={limited} data={data} />}
      {mode === 'list' && <List events={limited} data={data} />}
      {mode === 'agenda' && <Agenda events={limited} />}
      {mode === 'all' && (
        <AllEvents
          events={baseSorted}
          accordion={data.accordionByYear}
          pageSize={limit}
        />
      )}
    </div>
  );
};

export default View;
